/**
 * html2canvas clones DOM nodes but does not copy canvas bitmaps.
 * Preferred path: QR is already a PNG <img data-url>. Legacy canvas transfer remains as fallback.
 */

export const PDF_QR_CANVAS_ATTR = "data-pdf-qr-canvas";
/** Attribute on PNG data-URL QR images rendered in the closing page. */
export const PDF_QR_IMAGE_ATTR = "data-pdf-qr-image";

export type ProposalPdfCanvasTransferResult = {
  attempted: number;
  transferred: number;
  failed: number;
};

function canvasLooksDrawn(canvas: HTMLCanvasElement): boolean {
  if (canvas.width < 8 || canvas.height < 8) return false;
  try {
    const ctx = canvas.getContext("2d");
    if (!ctx) return false;
    const sample = ctx.getImageData(Math.floor(canvas.width / 2), Math.floor(canvas.height / 2), 1, 1).data;
    return sample[3] > 0 || sample[0] + sample[1] + sample[2] > 0;
  } catch {
    return canvas.width > 0 && canvas.height > 0;
  }
}

function findProposalPdfQrImages(root: ParentNode): HTMLImageElement[] {
  return Array.from(
    root.querySelectorAll<HTMLImageElement>(
      `img[${PDF_QR_IMAGE_ATTR}="true"], img[data-testid="dtools-pdf-closing-qr-image"]`,
    ),
  );
}

function qrImageLooksReady(img: HTMLImageElement): boolean {
  const src = (img.getAttribute("src") ?? img.currentSrc ?? "").trim();
  if (!src.startsWith("data:image/")) return false;
  if (src.length < 64) return false;
  if (img.getAttribute("data-pdf-image-failed") === "true") return false;
  return true;
}

/** Find canvases tagged for PDF QR export, falling back to any canvas under a QR host. */
export function findProposalPdfQrCanvases(root: ParentNode): HTMLCanvasElement[] {
  const tagged = Array.from(root.querySelectorAll<HTMLCanvasElement>(`canvas[${PDF_QR_CANVAS_ATTR}="true"]`));
  if (tagged.length > 0) return tagged;
  const hosts = Array.from(root.querySelectorAll<HTMLElement>('[data-testid="dtools-pdf-closing-qr"]'));
  const fromHosts: HTMLCanvasElement[] = [];
  for (const host of hosts) {
    const canvas = host.querySelector("canvas");
    if (canvas) fromHosts.push(canvas);
  }
  return fromHosts;
}

/**
 * Copy pixel buffers from source canvases into matching cloned canvases (by stable attribute / order).
 */
export function transferProposalPdfCanvasBitmaps(
  sourceRoot: HTMLElement,
  clonedRoot: HTMLElement,
): ProposalPdfCanvasTransferResult {
  const sourceCanvases = findProposalPdfQrCanvases(sourceRoot);
  const clonedCanvases = findProposalPdfQrCanvases(clonedRoot);
  let transferred = 0;
  let failed = 0;
  const count = Math.min(sourceCanvases.length, clonedCanvases.length);

  for (let i = 0; i < count; i += 1) {
    const source = sourceCanvases[i]!;
    const clone = clonedCanvases[i]!;
    try {
      if (source.width < 1 || source.height < 1) {
        failed += 1;
        continue;
      }
      clone.width = source.width;
      clone.height = source.height;
      const ctx = clone.getContext("2d");
      if (!ctx) {
        failed += 1;
        continue;
      }
      ctx.clearRect(0, 0, clone.width, clone.height);
      ctx.drawImage(source, 0, 0);
      if (!canvasLooksDrawn(clone)) {
        failed += 1;
        continue;
      }
      transferred += 1;
    } catch {
      failed += 1;
    }
  }

  return {
    attempted: count,
    transferred,
    failed: failed + Math.max(0, sourceCanvases.length - count),
  };
}

export type ProposalPdfQrReadiness = {
  required: boolean;
  ready: boolean;
  reason?: string;
};

/** Pre-capture readiness: QR host present ⇒ PNG data-URL image (or drawn canvas fallback) must be ready. */
export function assessProposalPdfQrReadiness(root: HTMLElement): ProposalPdfQrReadiness {
  const hosts = Array.from(root.querySelectorAll<HTMLElement>('[data-testid="dtools-pdf-closing-qr"]'));
  if (hosts.length === 0) {
    return { required: false, ready: true };
  }

  for (const host of hosts) {
    if (host.getAttribute("data-pdf-qr-failed") === "true") {
      return { required: true, ready: false, reason: "QR generation failed" };
    }
    const img = host.querySelector<HTMLImageElement>(
      `img[${PDF_QR_IMAGE_ATTR}="true"], img[data-testid="dtools-pdf-closing-qr-image"]`,
    );
    if (img) {
      if (!qrImageLooksReady(img)) {
        return { required: true, ready: false, reason: "QR image data URL missing or empty" };
      }
      continue;
    }
    const canvas = host.querySelector("canvas");
    if (canvas) {
      if (!canvasLooksDrawn(canvas)) {
        return { required: true, ready: false, reason: "QR canvas is blank or not yet drawn" };
      }
      continue;
    }
    if (host.getAttribute("aria-busy") === "true") {
      return { required: true, ready: false, reason: "QR image still generating" };
    }
    return { required: true, ready: false, reason: "QR host present but no image or canvas found" };
  }

  return { required: true, ready: true };
}

/** Replace a blank/unreliable cloned QR canvas with a PNG data-URL image from the source. */
export function replaceClonedQrCanvasWithImage(
  sourceCanvas: HTMLCanvasElement,
  clonedCanvas: HTMLCanvasElement,
): boolean {
  try {
    const dataUrl = sourceCanvas.toDataURL("image/png");
    if (!dataUrl || dataUrl.length < 32) return false;
    const img = clonedCanvas.ownerDocument.createElement("img");
    img.src = dataUrl;
    img.alt = "Proposal QR code";
    img.width = sourceCanvas.width;
    img.height = sourceCanvas.height;
    img.style.width = `${sourceCanvas.width}px`;
    img.style.height = `${sourceCanvas.height}px`;
    img.style.display = "block";
    img.setAttribute("data-testid", "dtools-pdf-closing-qr-image");
    img.setAttribute(PDF_QR_IMAGE_ATTR, "true");
    img.setAttribute(PDF_QR_CANVAS_ATTR, "image");
    clonedCanvas.replaceWith(img);
    return true;
  } catch {
    return false;
  }
}

/**
 * Preferred export path: QR is already a data-URL <img> (no canvas clone needed).
 * Legacy: transfer bitmap; if drawImage fails, fall back to data-URL <img>.
 */
export function prepareProposalPdfQrForClone(
  sourceRoot: HTMLElement,
  clonedRoot: HTMLElement,
): ProposalPdfCanvasTransferResult {
  const sourceImages = findProposalPdfQrImages(sourceRoot);
  const clonedImages = findProposalPdfQrImages(clonedRoot);
  if (sourceImages.length > 0 && clonedImages.length > 0) {
    let ready = 0;
    const count = Math.min(sourceImages.length, clonedImages.length);
    for (let i = 0; i < count; i += 1) {
      const source = sourceImages[i]!;
      const clone = clonedImages[i]!;
      if (qrImageLooksReady(source)) {
        const src = source.getAttribute("src") ?? source.currentSrc;
        if (src && (clone.getAttribute("src") ?? "") !== src) {
          clone.setAttribute("src", src);
        }
        ready += 1;
      }
    }
    if (ready > 0) {
      return { attempted: count, transferred: ready, failed: Math.max(0, count - ready) };
    }
  }

  const transfer = transferProposalPdfCanvasBitmaps(sourceRoot, clonedRoot);
  if (transfer.failed === 0 && transfer.transferred > 0) return transfer;

  const sourceCanvases = findProposalPdfQrCanvases(sourceRoot);
  const clonedCanvases = findProposalPdfQrCanvases(clonedRoot);
  let rescued = 0;
  const count = Math.min(sourceCanvases.length, clonedCanvases.length);
  for (let i = 0; i < count; i += 1) {
    if (replaceClonedQrCanvasWithImage(sourceCanvases[i]!, clonedCanvases[i]!)) rescued += 1;
  }
  return {
    attempted: Math.max(transfer.attempted, count),
    transferred: transfer.transferred + rescued,
    failed: Math.max(0, Math.max(transfer.attempted, count) - (transfer.transferred + rescued)),
  };
}

export const PDF_QR_VALUE_ATTR = "data-pdf-qr-value";

function resolveQrValueFromHost(host: HTMLElement): string {
  const tagged = (host.getAttribute(PDF_QR_VALUE_ATTR) ?? "").trim();
  if (tagged) return tagged;
  const link = host.closest("[data-testid='dtools-pdf-closing-page']")?.querySelector<HTMLAnchorElement>(
    "[data-testid='dtools-pdf-closing-url']",
  );
  return (link?.getAttribute("href") ?? "").trim();
}

function mountQrDataUrlImage(host: HTMLElement, dataUrl: string): void {
  host.removeAttribute("aria-busy");
  host.removeAttribute("data-pdf-qr-failed");
  let img = host.querySelector<HTMLImageElement>(
    `img[${PDF_QR_IMAGE_ATTR}="true"], img[data-testid="dtools-pdf-closing-qr-image"]`,
  );
  if (!img) {
    img = host.ownerDocument.createElement("img");
    img.alt = "Proposal QR code";
    img.width = 160;
    img.height = 160;
    img.decoding = "sync";
    img.loading = "eager";
    img.setAttribute("data-testid", "dtools-pdf-closing-qr-image");
    img.setAttribute(PDF_QR_IMAGE_ATTR, "true");
    img.style.display = "block";
    img.style.width = "160px";
    img.style.height = "160px";
    host.replaceChildren(img);
  }
  img.src = dataUrl;
  img.setAttribute("src", dataUrl);
}

/**
 * Export-safe QR: generate a PNG data-URL and mount a normal <img>.
 * Never leaves an empty placeholder when a URL is available.
 */
export async function ensureProposalPdfQrImages(root: HTMLElement): Promise<ProposalPdfQrReadiness> {
  const hosts = Array.from(root.querySelectorAll<HTMLElement>('[data-testid="dtools-pdf-closing-qr"]'));
  if (hosts.length === 0) {
    return { required: false, ready: true };
  }

  const QRCode = (await import("qrcode")).default;

  for (const host of hosts) {
    const existing = host.querySelector<HTMLImageElement>(
      `img[${PDF_QR_IMAGE_ATTR}="true"], img[data-testid="dtools-pdf-closing-qr-image"]`,
    );
    if (existing && qrImageLooksReady(existing)) {
      host.removeAttribute("aria-busy");
      host.removeAttribute("data-pdf-qr-failed");
      continue;
    }

    const value = resolveQrValueFromHost(host);
    if (!value) {
      return { required: true, ready: false, reason: "QR host present but proposal URL missing" };
    }

    try {
      const dataUrl = await QRCode.toDataURL(value, {
        width: 360,
        margin: 2,
        errorCorrectionLevel: "M",
        color: { dark: "#1A2744", light: "#FFFFFF" },
      });
      if (!dataUrl || !dataUrl.startsWith("data:image/") || dataUrl.length < 64) {
        return { required: true, ready: false, reason: "QR generation returned empty image" };
      }
      mountQrDataUrlImage(host, dataUrl);
    } catch {
      return { required: true, ready: false, reason: "QR generation threw" };
    }
  }

  return assessProposalPdfQrReadiness(root);
}
